import { useState } from "react";
import type { FormEvent } from "react";
import { adminApi } from "@/lib/api/endpoints";
import { useAsync } from "@/lib/hooks/useAsync";
import {
  Badge, Button, Card, CardSection, ConfirmModal, EmptyState, Field, Input, Modal, Select, useToast,
} from "@/components/ui";
import { formatDate } from "@/lib/utils/format";
import { useAuth } from "@/features/auth/AuthContext";
import type { User } from "@/lib/types/api";
import { IconPlus, IconUsers } from "../components/icons";

/**
 * Team management.
 *
 * Accounts are created outright with a password the org admin types and hands
 * over — there are no invitation emails. The previous flow emailed a
 * set-password link, which never arrived: mail is configured to write to a file
 * rather than send, so every invited account was unusable and sat forever at
 * "Invitation pending".
 *
 * The consequence to keep in mind: this page is the only way into a staff
 * account. If someone forgets their password, an org admin sets a new one here,
 * and doing so signs them out everywhere.
 */

/** Long enough to be worth having, short enough to read out loud once. */
function suggestPassword(): string {
  const alphabet = "abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const bytes = crypto.getRandomValues(new Uint32Array(14));
  return Array.from(bytes, (n) => alphabet[n % alphabet.length]).join("");
}
export function TeamPage() {
  const { user: currentUser } = useAuth();
  const toast = useToast();
  const users = useAsync(() => adminApi.users.list(), []);

  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState<User | null>(null);
  const [removing, setRemoving] = useState<User | null>(null);
  const [busy, setBusy] = useState(false);

  async function remove() {
    if (!removing) return;
    setBusy(true);
    try {
      await adminApi.users.remove(removing.id);
      toast.success(`${removing.email} removed`);
      users.reload();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not remove that user");
    } finally {
      setBusy(false);
      setRemoving(null);
    }
  }

  return (
    <CardSection
      eyebrow="Access"
      title="Team"
      description="People who can sign in to this organization's console. You set their password and give it to them — nothing is emailed."
      actions={<Button variant="primary" size="sm" icon={<IconPlus size={14} />} onClick={() => setCreating(true)}>Add member</Button>}
    >
      {users.loading ? (
        <div className="skeleton h-48" />
      ) : (users.data ?? []).length === 0 ? (
        <EmptyState icon={<IconUsers size={28} />} title="No team members" />
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-600 text-left">
                {["Email", "Role", "Status", "Added", ""].map((heading) => (
                  <th key={heading} className="label-eyebrow px-5 py-3">{heading}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(users.data ?? []).map((user) => (
                <tr key={user.id} className="border-b border-ink-600/60 last:border-0">
                  <td className="px-5 py-3 text-haze-100">
                    {user.email}
                    {user.id === currentUser?.id && <span className="ml-2 text-xs text-haze-400">(you)</span>}
                  </td>
                  <td className="px-5 py-3">
                    <Badge tone={ROLE_BADGE[user.role] ?? "neutral"}>
                      {ROLE_LABEL[user.role] ?? user.role}
                    </Badge>
                    {/* An event admin's access is meaningless without knowing
                        which events, so name them rather than making someone
                        open each event to find out. */}
                    {user.role === "event_admin" && (
                      <p className="mt-1 text-2xs text-haze-400">
                        {user.event_access && user.event_access.length > 0
                          ? user.event_access.map((entry) => entry.event_name).join(", ")
                          : "No events assigned yet"}
                      </p>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    {user.invitation_pending ? (
                      // Created by the old invite flow, whose email never sent.
                      // They cannot sign in until someone sets a password.
                      <Badge tone="warn">Needs a password</Badge>
                    ) : (
                      <Badge tone="ok">Active</Badge>
                    )}
                  </td>
                  <td className="px-5 py-3 text-haze-400">{formatDate(user.created_at)}</td>
                  <td className="px-5 py-3">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="ghost" onClick={() => setResetting(user)}>
                        Set password
                      </Button>
                      {user.id !== currentUser?.id && (
                        <Button size="sm" variant="ghost" className="text-haze-400 hover:text-danger" onClick={() => setRemoving(user)}>
                          Remove
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <CreateMemberModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={users.reload}
        canGrantSuperadmin={currentUser?.role === "superadmin"}
      />

      <SetPasswordModal
        user={resetting}
        onClose={() => setResetting(null)}
        onSaved={users.reload}
      />

      <ConfirmModal
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        onConfirm={remove}
        loading={busy}
        destructive
        title={`Remove ${removing?.email}?`}
        description="They lose access immediately and any active session is revoked. Events and generations they created are unaffected."
        confirmLabel="Remove member"
      />
    </CardSection>
  );
}

const ROLE_LABEL: Record<string, string> = {
  superadmin: "Superadmin",
  org_admin: "Admin",
  event_admin: "Event admin",
};

const ROLE_BADGE: Record<string, "accent" | "neutral" | "warn"> = {
  superadmin: "accent",
  org_admin: "neutral",
  event_admin: "warn",
};

const ROLE_HINT: Record<string, string> = {
  org_admin: "Full access to every event in this organization.",
  event_admin: "Access only to the events you assign them to, with permissions you choose per event.",
  superadmin: "Can access every organization on the platform.",
};

function CreateMemberModal({
  open, onClose, onCreated, canGrantSuperadmin,
}: { open: boolean; onClose: () => void; onCreated: () => void; canGrantSuperadmin: boolean }) {
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState(suggestPassword);
  const [role, setRole] = useState("org_admin");
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    try {
      await adminApi.users.create({ email, role, password });
      toast.success(`${email} can sign in now`);
      setEmail("");
      setPassword(suggestPassword());
      onCreated();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not create that account");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a team member"
      description="Their account works immediately. Nothing is emailed — copy the password and give it to them."
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Email address" required>
          <Input type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>

        <Field label="Password" required hint="At least 8 characters. They can change it after signing in.">
          <div className="flex gap-2">
            {/* Deliberately not a masked field. The person creating the account
                has to read this out or copy it, and hiding it from them helps
                nobody — the risk here is a typo they can't see, not a shoulder
                surfer. */}
            <Input
              type="text"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="font-mono"
            />
            <Button type="button" variant="outline" onClick={() => setPassword(suggestPassword())}>
              New
            </Button>
          </div>
        </Field>

        <Field label="Role" hint={ROLE_HINT[role] ?? ""}>
          <Select value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="org_admin">Admin</option>
            <option value="event_admin">Event admin</option>
            {canGrantSuperadmin && <option value="superadmin">Superadmin</option>}
          </Select>
        </Field>

        {role === "event_admin" && (
          <p className="rounded-lg border border-ink-600 bg-ink-800/60 px-3 py-2 text-xs text-haze-300">
            They'll have access to nothing until you assign them to an event.
            Open the event, then the <strong>Access</strong> tab.
          </p>
        )}

        <div className="mt-2 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={submitting} disabled={!email || password.length < 8}>
            Create account
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Sets a new password for someone.
 *
 * With no invitation emails there is also no "forgot password" email, so this
 * is the only recovery path. It signs them out of every device, which is what
 * you want when the reason is a departure or a leak.
 */
function SetPasswordModal({
  user, onClose, onSaved,
}: { user: User | null; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [password, setPassword] = useState(suggestPassword);
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!user) return;
    setSubmitting(true);
    try {
      await adminApi.users.update(user.id, { password });
      toast.success(`New password set for ${user.email}`);
      setPassword(suggestPassword());
      onSaved();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not set that password");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={Boolean(user)}
      onClose={onClose}
      title={`Set a password for ${user?.email ?? ""}`}
      description="They'll be signed out everywhere and will need this to sign back in."
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="New password" required hint="At least 8 characters.">
          <div className="flex gap-2">
            <Input
              type="text"
              required
              minLength={8}
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="font-mono"
            />
            <Button type="button" variant="outline" onClick={() => setPassword(suggestPassword())}>
              New
            </Button>
          </div>
        </Field>

        <div className="mt-2 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={submitting} disabled={password.length < 8}>
            Set password
          </Button>
        </div>
      </form>
    </Modal>
  );
}
